import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { CgpSnackbarService } from '../cgp-snackbar/cgp-snackbar.service';

import { FileDownloadApiService } from '../../../modules/api';
import { BaseHttpService } from '../base-http/base-http.service';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

@Injectable()
export class FileDownloadService {
   readonly _snackbarErrorMessage = 'An error occured. Please try again.';
   readonly _snackbarNoFileMessage = 'No file exists. Please try again.';
   readonly _snackbarNoDataMessage = 'This program has no data.';
   readonly _snackbarAction = 'Dismiss';

   constructor(
      private readonly toastr: ToastrService,
      private readonly cgpSnackbarService: CgpSnackbarService,
      private readonly fileDownloadApiService: FileDownloadApiService,
      private readonly baseHttpService: BaseHttpService,
   ) { }

   downloadFile(url: string, filename?: string) {
      return new Observable((observer) =>
         this.fileDownloadApiService.downloadFile(url)
            .pipe(
               finalize(observer.complete.bind(observer))
            ).subscribe(
               (response) => {
                  // Handle null response
                  response = response || {};

                  filename = filename || response.name || 'file';
                  if (response.data) {
                     this.downloadFileFromBlob(filename, this.convertAsciiToBlob(response.data));
                     observer.next(true);
                  } else {
                     this.toastr.error(`No data was received from server for ${filename}`);
                     observer.next(false);
                  }
               },
               error => observer.next(false))
      );
   }

   downloadExcel(url, body, { options = undefined, filename = '' } = {}) {
      return new Observable((observer) =>
         this.baseHttpService.post(url, body, options)
            .pipe(finalize(observer.complete.bind(observer)))
            .subscribe(
               (res) => {
                  if (res && res.data) {
                     // Default file name
                     if (!filename) {
                        filename = `TPRECM_File`;

                        // Append the current day's date to the file name
                        const date = new Date();
                        const formattedMonth = `0${date.getUTCMonth() + 1}`.slice(-2);
                        const formattedDay = `0${date.getUTCDate()}`.slice(-2);
                        const formattedDate = `${date.getUTCFullYear()}-${formattedMonth}-${formattedDay}`;
                        filename += `_${formattedDate}`;
                     }

                     this.downloadBlob(filename, this.b642Blob(res.data, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'));
                     observer.next(true);
                  } else {
                     this.toastr.error(this._snackbarErrorMessage, 'Download failed.');
                     observer.next(false);
                  }
               },
               error => observer.error(false)
            ));
   }

   downloadFileWithBody(filename, url, params) {
      return new Observable((observer) => {
         this.fileDownloadApiService.downloadFileWithBody(url, params)
            .pipe(finalize(observer.complete.bind(observer)))
            .subscribe(
               response => {
                  if (response.data) {
                     this.downloadFileFromBlob(filename, this.convertAsciiToBlob(response.data));
                     observer.next(true);
                  } else {
                     this.toastr.error(`No data was received from server for ${filename}`);
                     observer.next(false);
                  }
               },
               error => {
                  if (error && error.data) {
                     this.toastr.error(error.data);
                  }

                  observer.next(false);
               }
            );
      });
   }

   downloadBlob(filename, blob) {
      if (blob.size === 0) {
         this.toastr.error(this._snackbarNoDataMessage);
         return;
      }
      const a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
   }

   downloadFileFromBlob(filename, blob) {
      if (blob.size === 0) {
         this.cgpSnackbarService.showSnackbar({
            message: this._snackbarNoFileMessage,
            action: this._snackbarAction,
         });
         return;
      }

      const a = window.document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
   }

   convertAsciiToBlob(ascii) {
      const fileBinary = window.atob(ascii);
      const bytes = new Uint8Array(fileBinary.length);

      for (let i = 0; i < fileBinary.length; i++) {
         bytes[i] = fileBinary.charCodeAt(i);
      }

      return new Blob([bytes]);
   }

   /**
    * Convert a base-64 encoded string to a Blob.
    *
    * @private
    * @memberof FileDownloadService
    */
   b642Blob(b64String: string, contentType = ''): Blob {
      const byteChars = atob(b64String);
      const byteArrays = [];
      // Arbitrary number for each slice, tends to work well.
      // The sliceSize improves performance by processing
      // the byteChars array in pieces and not all at once.
      const sliceSize = 512;
      for (let offset = 0; offset < byteChars.length; offset += sliceSize) {
         const slice = byteChars.slice(offset, offset + sliceSize);

         const byteNumbers = new Array(slice.length);
         for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
         }
         const byteArray = new Uint8Array(byteNumbers);
         byteArrays.push(byteArray);
      }
      return new Blob(byteArrays, { type: contentType });
   }
}
